import { z } from "zod"
import isEmailValidAndSingle, { emailValidationResponse } from "~/utils/isEmailValidAndSingle"

export const campInfoDefaultValues = {
  name: "",
  primary_color: "",
  secondary_color: "",
  email: "",
  phone: "",
  address: "",
  city: "",
  state: "",
  zip: "",
  bio: "",
  notification_emails: [],
  season_start_mm: "",
  season_start_dd: "",
  season_end_mm: "",
  season_end_dd: "",
  booking_start_mm: "",
  booking_start_dd: "",
  gate_code: "",
  bathroom_code: "",
  laundry_code: "",
  wifi_network: "",
  wifi_password: "",
  allow_same_day_reservations: false,
  allow_rv_on_tent_site: false,
  same_day_reservations_cutoff_time: "",
  stripe_account_id: "",
  slug: "",
  shortened_slug: "",
  timezone_offset: "",
  timezone: "",
  bookable_for_n_days: undefined,
  date_format: "",
  map_url: "",
  logo_url: "",
}

export const CampInfoSchema = z
  .object({
    name: z.string(),
    email: z.string().nonempty({ message: "Email is required" }).email({ message: "Invalid email address" }),
    primary_color: z.string().optional(),
    secondary_color: z.string().optional(),
    phone: z.string().optional(),
    address: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    zip: z.string().optional(),
    bio: z.string().optional().nullable(),
    notification_emails: z
      .array(
        z
          .string()
          .nonempty({ message: "Notification email cannot be empty" })
          .email({ message: "Invalid notification email address" }),
      )
      .optional(),
    season_start_mm: z.string().optional().nullable(),
    season_start_dd: z.string().optional().nullable(),
    season_end_mm: z.string().optional().nullable(),
    season_end_dd: z.string().optional().nullable(),
    booking_start_mm: z.string().optional().nullable(),
    booking_start_dd: z.string().optional().nullable(),
    gate_code: z.string().optional().nullable(),
    bathroom_code: z.string().optional().nullable(),
    laundry_code: z.string().optional().nullable(),
    wifi_network: z.string().optional().nullable(),
    wifi_password: z.string().optional().nullable(),
    allow_same_day_reservations: z.boolean(),
    allow_rv_on_tent_site: z.boolean(),
    same_day_reservations_cutoff_time: z.string().nullable(),
    stripe_account_id: z.string().nullable(),
    slug: z.string().nullable(),
    shortened_slug: z.string().nullable(),
    timezone_offset: z.string().nullable(),
    timezone: z.string().nullable(),
    bookable_for_n_days: z.number().nullable(),
    date_format: z.string().nullable(),
    map_url: z.string().optional().nullable(),
    logo_url: z.string().optional().nullable(),
  })
  .superRefine((data, ctx) => {
    const allFields = [data.season_start_mm, data.season_start_dd, data.season_end_mm, data.season_end_dd]

    const isAllEmpty = allFields.every((v) => !v)
    const isAllFilled = allFields.every((v) => !!v)

    if (!isAllEmpty && !isAllFilled) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "All season date fields must be filled or all must be empty.",
        path: ["season_start_mm"],
      })
    }

    if (isAllFilled) {
      const start = new Date(2000, Number(data.season_start_mm) - 1, Number(data.season_start_dd))
      const end = new Date(2000, Number(data.season_end_mm) - 1, Number(data.season_end_dd))

      if (start >= end) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Season start must be before season end.",
          path: ["season_start_mm"],
        })
      }
    }
  })

export type CampInfoType = z.infer<typeof CampInfoSchema>
